import { getPlanCaptureContext } from '../render/plan_capture_context.mjs';
import {
  screenToWorld,
  setStatus
} from '../render/plan_renderer.js?v=20260904f';
import {
  combineProjectBounds,
  computeProjectBounds,
  normalizeProjectBounds
} from '../project/project_bounds_service.js?v=20260706d';
import { dimensionLayout } from '../services/dimension_service.js?v=20260904f';
import {
  ensureObstacleState,
  obstacleBounds
} from '../obstacles/obstacles.js?v=20260903c';
import { S } from '../app/app_state.js?v=20260817c';

const LEFT_SIDEBAR_STORAGE_KEY = 'ksStudio.leftSidebarCollapsed';
const LEFT_SIDEBAR_SCROLL_HINT_TOLERANCE_PX = 1;
const FIT_VIEWPORT_FILL = 0.9;
const FIT_MIN_SCALE = 0.01;
const FIT_MAX_SCALE = 10;

function getCanvasViewportRect() {
  return document.getElementById('canvasWrap')?.getBoundingClientRect?.() || null;
}

function queueCanvasViewportLock() {
  const rect = getCanvasViewportRect();
  const scale = Number(S?.camera2D?.scale);
  if (!rect || !Number.isFinite(rect.width) || !Number.isFinite(rect.height) || !Number.isFinite(scale) || scale <= 0) return;
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.pendingCanvasViewportLock = {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height
  };
}

export function restorePendingCanvasViewportLock() {
  const pending = S.ui?.pendingCanvasViewportLock;
  if (!pending) return false;
  delete S.ui.pendingCanvasViewportLock;
  const rect = getCanvasViewportRect();
  const scale = Number(S?.camera2D?.scale);
  if (
    !rect
    || !Number.isFinite(rect.left)
    || !Number.isFinite(rect.top)
    || !Number.isFinite(rect.width)
    || !Number.isFinite(rect.height)
    || !Number.isFinite(scale)
    || scale <= 0
  ) {
    return false;
  }
  const dx = (rect.left - pending.left) + ((rect.width - pending.width) / 2);
  const dz = (rect.top - pending.top) + ((rect.height - pending.height) / 2);
  S.camera2D.x += dx / scale;
  S.camera2D.z += dz / scale;
  return true;
}

export function ensureCanvasLayout() {
  const shell = document.querySelector('.app-shell');
  const left = document.querySelector('.sidebar.left');
  const workspace = document.querySelector('.workspace');
  const right = document.querySelector('.sidebar.right');
  const canvas = document.getElementById('canvasWrap');
  if (!shell || !workspace || !canvas) return;

  shell.style.minHeight = '0';
  shell.style.gridTemplateRows = 'minmax(0,1fr)';
  shell.style.gridTemplateColumns = '';
  workspace.style.minWidth = '0';
  workspace.style.minHeight = '0';
  workspace.style.gridColumn = '';
  workspace.style.gridRow = '';
  canvas.style.minWidth = '0';
  canvas.style.minHeight = '220px';
  if (left) {
    left.style.gridColumn = '';
    left.style.gridRow = '';
  }
  if (right) {
    right.style.display = '';
    right.style.gridColumn = '';
    right.style.gridRow = '';
    right.style.maxHeight = '';
  }
}

function syncLeftSidebarToggle(collapsed) {
  const button = document.getElementById('btnToggleLeftSidebar');
  if (!button) return;
  button.textContent = collapsed ? '▶' : '◀';
  button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  button.title = collapsed ? 'Linke Seitenleiste einblenden' : 'Linke Seitenleiste einklappen';
  button.setAttribute('aria-label', button.title);
}

export function setLeftSidebarCollapsed(collapsed, options = {}) {
  const shell = document.querySelector('.app-shell');
  if (!shell) return false;
  const nextCollapsed = Boolean(collapsed);
  if (options.preserveViewport !== false) queueCanvasViewportLock();
  shell.classList.toggle('sidebar-collapsed', nextCollapsed);
  syncLeftSidebarToggle(nextCollapsed);
  scheduleLeftSidebarScrollHintSync();
  if (options.persist !== false) {
    try {
      window.localStorage?.setItem(LEFT_SIDEBAR_STORAGE_KEY, nextCollapsed ? '1' : '0');
    } catch (_error) {
      // LocalStorage kann in privaten/gesperrten Kontexten fehlschlagen.
    }
  }
  ensureCanvasLayout();
  if (options.notify !== false) {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }
  return nextCollapsed;
}

export function applyStoredLeftSidebarState() {
  let collapsed;
  try {
    collapsed = window.localStorage?.getItem(LEFT_SIDEBAR_STORAGE_KEY) === '1';
  } catch (_error) {
    collapsed = false;
  }
  return setLeftSidebarCollapsed(collapsed, { persist: false, notify: false });
}

export function bindLeftSidebarToggle() {
  applyStoredLeftSidebarState();
  bindLeftSidebarScrollHints();
  const button = document.getElementById('btnToggleLeftSidebar');
  if (!button) return;
  button.addEventListener('click', () => {
    const shell = document.querySelector('.app-shell');
    setLeftSidebarCollapsed(!shell?.classList.contains('sidebar-collapsed'));
  });
}

let leftSidebarScrollHintFrame = 0;

function setScrollHintVisible(hint, visible) {
  if (hint.hidden === !visible) return;
  hint.hidden = !visible;
}

function syncLeftSidebarScrollHints() {
  leftSidebarScrollHintFrame = 0;
  const sidebar = document.getElementById('leftSidebar');
  const upHint = document.getElementById('leftSidebarScrollUpHint');
  const downHint = document.getElementById('leftSidebarScrollDownHint');
  if (!sidebar || !upHint || !downHint) return;
  const collapsed = Boolean(sidebar.closest('.app-shell')?.classList.contains('sidebar-collapsed'));
  const scrollTop = Math.max(0, sidebar.scrollTop);
  const maxScrollTop = Math.max(0, sidebar.scrollHeight - sidebar.clientHeight);
  setScrollHintVisible(upHint, !collapsed && scrollTop > LEFT_SIDEBAR_SCROLL_HINT_TOLERANCE_PX);
  setScrollHintVisible(downHint, !collapsed && scrollTop < maxScrollTop - LEFT_SIDEBAR_SCROLL_HINT_TOLERANCE_PX);
}

function scheduleLeftSidebarScrollHintSync() {
  if (leftSidebarScrollHintFrame || typeof requestAnimationFrame !== 'function') return;
  leftSidebarScrollHintFrame = requestAnimationFrame(syncLeftSidebarScrollHints);
}

// Der Scrollbalken der linken Seitenleiste ist ausgeblendet; die orangen
// Hinweise zeigen stattdessen, ob oberhalb bzw. unterhalb noch Inhalt liegt.
// Der ResizeObserver erfasst sowohl Fensteraenderungen als auch neu
// gerenderte Projekt-/Gruppen-/Objektkarten.
function bindLeftSidebarScrollHints() {
  const sidebar = document.getElementById('leftSidebar');
  if (!sidebar) return;
  sidebar.addEventListener('scroll', scheduleLeftSidebarScrollHintSync, { passive: true });
  if (typeof ResizeObserver === 'function') {
    const observer = new ResizeObserver(scheduleLeftSidebarScrollHintSync);
    observer.observe(sidebar);
    for (const section of sidebar.children) observer.observe(section);
  } else {
    window.addEventListener('resize', scheduleLeftSidebarScrollHintSync);
  }
  scheduleLeftSidebarScrollHintSync();
}

export function startPan(e) {
  e.preventDefault();
  const svg = document.getElementById('planSvg');
  const rect = svg.getBoundingClientRect();
  const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
  S.drag = {
    active: true,
    type: 'pan',
    startMouse: world,
    startScreen: { x: e.clientX, y: e.clientY },
    startScale: S.camera2D.scale,
    camStart: { x: S.camera2D.x, z: S.camera2D.z }
  };
  setStatus('Plan wird verschoben');
}

function pointBounds(point, padding = 1) {
  const x = Number(point?.x);
  const z = Number(point?.z ?? point?.y);
  if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
  return {
    minX: x - padding,
    minZ: z - padding,
    maxX: x + padding,
    maxZ: z + padding
  };
}

function dimensionBounds(dimension) {
  try {
    const layout = dimensionLayout(dimension);
    return combineProjectBounds([
      pointBounds(layout.pointA),
      pointBounds(layout.pointB),
      pointBounds(layout.dimA),
      pointBounds(layout.dimB),
      pointBounds(layout.textPosition)
    ].filter(Boolean), { defaultBounds: null });
  } catch (_error) {
    return combineProjectBounds([
      pointBounds(dimension?.pointA),
      pointBounds(dimension?.pointB)
    ].filter(Boolean), { defaultBounds: null });
  }
}

function collectCorePlanBounds() {
  const entries = [];
  if (Array.isArray(S.project?.dimensions)) {
    for (const dimension of S.project.dimensions) {
      const bounds = dimensionBounds(dimension);
      if (bounds) entries.push(bounds);
    }
  }
  if (Array.isArray(S.project?.textAnnotations)) {
    for (const annotation of S.project.textAnnotations) {
      const bounds = pointBounds(annotation, 80);
      if (bounds) entries.push(bounds);
    }
  }
  try {
    for (const obstacle of ensureObstacleState()) {
      const bounds = normalizeProjectBounds(obstacleBounds(obstacle));
      if (bounds) entries.push(bounds);
    }
  } catch (_error) {
    // Hindernisse duerfen den globalen Kamera-Fit nicht blockieren.
  }
  return entries;
}

export function fitCameraToAllPlanContent(options = {}) {
  const capture = getPlanCaptureContext();
  const rect = capture ? { width: capture.size.w, height: capture.size.h }
    : document.getElementById('planSvg')?.getBoundingClientRect?.() || getCanvasViewportRect();
  if (!rect || rect.width <= 0 || rect.height <= 0) return false;
  const requestedBounds = normalizeProjectBounds(options.bounds);
  const bounds = requestedBounds || combineProjectBounds([
    computeProjectBounds({ defaultBounds: null }),
    ...collectCorePlanBounds()
  ].filter(Boolean), { defaultBounds: null });
  if (!bounds) return false;

  const width = Math.max(1, bounds.maxX - bounds.minX);
  const depth = Math.max(1, bounds.maxZ - bounds.minZ);
  const fill = Number.isFinite(Number(options.fill)) ? Number(options.fill) : FIT_VIEWPORT_FILL;
  const rawScale = Math.min((rect.width * fill) / width, (rect.height * fill) / depth);
  const scale = Math.min(FIT_MAX_SCALE, Math.max(FIT_MIN_SCALE, rawScale));
  S.camera2D.x = (bounds.minX + bounds.maxX) / 2;
  S.camera2D.z = (bounds.minZ + bounds.maxZ) / 2;
  S.camera2D.scale = scale;
  setStatus('Ansicht auf alle Zeichnungen angepasst');
  return true;
}

export function releaseCanvasKeyboardFocus() {
  const active = document.activeElement;
  if (!active || active === document.body) return;
  if (typeof active.blur === 'function') active.blur();
}
